import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SendMessageModal from './SendMessageModal';

// In-depth guides for forms people search for (keep in sync with scripts/seo-data.js).
const FORM_GUIDES = [
  [/dissolution/i, '/blog/nc-articles-of-dissolution-form-l-07-guide'],
  [/reinstat/i, '/blog/how-to-reinstate-a-dissolved-llc-in-north-carolina'],
  [/5471/, '/blog/form-5471-explained-schedules-and-filing'],
];

const FormDetails = () => {
  const { formName } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const isDeadlinePassed = (deadlineDate) => {
    if (!deadlineDate) return false;
    const today = new Date();
    const deadline = new Date(deadlineDate);
    return deadline < today;
  };

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true);
        // Fetch all forms and find the one matching the URL parameter
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/forms/`);
        
        // Find the form regardless of parentheses in the name
        const formRecord = response.data.find(form => {
          // Process the form name to remove parentheses and their content for comparison
          const processedFormName = form.form_name
            .replace(/\s*\([^)]*\)\s*/g, ' ')
            .trim()
            .toLowerCase()
            .replace(/[\s,\.]/g, '-')
            .replace(/--+/g, '-');
          
          return processedFormName === formName;
        });
        
        if (formRecord) {
          setFormData(formRecord);
          
          // Track page view for this form detail page with GTM dataLayer
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'page_view',
            page_title: `${formRecord.form_name} - Form Details`,
            page_location: window.location.href,
            page_path: window.location.pathname,
            form_name: formRecord.form_name,
            form_category: formRecord.form_category || 'tax_filing'
          });
        } else {
          setError('Form not found');
        }
      } catch (err) {
        setError('Failed to load form data');
        console.error('Error fetching form data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, [formName]);

  const handleStartFiling = () => {
    navigate(`/filing-purchase?filing=${formData.id}`);
  };
  
  const openSendMessageModal = () => {
    setShowModal(true);
  };
  
  const closeSendMessageModal = () => {
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="form-details-container">
        <div className="form-details-loading">Loading form details...</div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="form-details-container">
        <div className="form-details-error">
          <h2>Error</h2>
          <p>{error || 'Form not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/filings')}>
            View All Filings
          </button>
        </div>
      </div>
    );
  }
  
  const deadlinePassed = isDeadlinePassed(formData.deadline_date);

  return (
    <div className="form-details-container">
      <div className="form-details-header">
        <h1>{formData.form_name}</h1>
      </div>
      
      <div className="form-details-content">
        <div className="form-details-main">
          <p className="form-details-short-desc">{formData.short_description}</p>
          
          <div className="form-details-long-desc">
            <h3>Description</h3>
            <p>{formData.long_description}</p>
            {(() => {
              const guide = (FORM_GUIDES.find(([re]) => re.test(formData.form_name)) || [])[1];
              return guide ? <p><Link to={guide}>Read our full guide to {formData.form_name}</Link></p> : null;
            })()}
          </div>
          
          {deadlinePassed ? (
            <>
              <div className="deadline-warning">
                <p>The deadline for this filing has passed. Please send us a message for assistance.</p>
              </div>
              <button 
                className="form-details-cta-button send-message-button" 
                onClick={openSendMessageModal}
              >
                Send Message
              </button>
            </>
          ) : (
            <button 
              className="form-details-cta-button" 
              onClick={handleStartFiling}
            >
              Start Filing
            </button>
          )}
        </div>
        
        <div className="form-details-sidebar">
          <div className="form-details-info-card">
            <div className="form-info-item">
              <h4>Filing Frequency</h4>
              <p>{formData.filing_frequency}</p>
            </div>
            
            <div className="form-info-item">
              <h4>Estimated Fee</h4>
              <p className="form-fee">{formData.estimated_fee}</p>
              <p className="form-fee-note">Once you purchase a filing, one of our team members will reach out to you to get your entity details to file.</p>
            </div>
            
            {formData.deadline_date && (
              <div className="form-info-item">
                <h4>Deadline</h4>
                <p className={`form-deadline ${deadlinePassed ? 'passed' : ''}`}>
                  {new Date(formData.deadline_date).toLocaleDateString()}
                  {deadlinePassed && <span className="deadline-label"> (Passed)</span>}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {showModal && (
        <SendMessageModal 
          formName={formData.form_name}
          onClose={closeSendMessageModal}
        />
      )}
    </div>
  );
};

export default FormDetails; 